// Mon activité (canvas A1 à A5, 24/10/2026) : ce que la personne a fait depuis son téléphone, les
// actions les plus récentes en haut, groupées par jour. Onglet Équipe pour qui peut lire toute l'équipe.
// Entrée : { id, kind: 'inventory' | 'intervention' | 'tour' | 'comment', time, user, userName,
//            location, title, detail, result: { tone, label } | null, tourId, snapshot }

import { formatShortDate, DAY } from './fieldDates'
import { normalizeLocation } from './fieldSearch'
import { plural, referenceLabel, objectCount } from './fieldReference'
import { duringCommentItems, duringPhotoItems } from './fieldNotes'
import { guestName } from './fieldStays'

export const FILTERS = [
	{ id: 'all', label: 'Tous', kinds: null },
	{ id: 'inventory', label: 'Inventaires', kinds: ['inventory', 'comment'] },
	{ id: 'tour', label: 'Tours', kinds: ['tour'] },
	{ id: 'intervention', label: 'Interventions', kinds: ['intervention'] }
]

const TYPE_TITLES = { arrival: 'Entrée', departure: 'Sortie', free: 'Hors séjour', tour: 'Tour' }

let sequence = 0

function nextId(kind, time)
{
	sequence++
	return kind + '-' + time + '-' + sequence
}

function userNameOf(user)
{
	return user ? ((user.firstname || '') + ' ' + (user.lastname || '')).trim() : ''
}

function timeLabel(seconds)
{
	const date = new Date(seconds * 1000)
	return String(date.getHours()).padStart(2, '0') + ':' + String(date.getMinutes()).padStart(2, '0')
}

// Résultat écrit en mots (charte : jamais d'information par la seule couleur)
export function inventoryResult(summary)
{
	if (!summary)
		return { tone: 'neutral', label: 'Photos et commentaire' }
	if (summary.gaps.length === 0)
		return { tone: 'ok', label: 'Conforme' }
	return { tone: 'gap', label: plural(summary.gaps.length, 'écart') }
}

export function inventoryEntry({ type, pitch, tour, user, time, version, inventoryId, summary, snapshot })
{
	return {
		id: nextId('inventory', time),
		kind: 'inventory',
		time,
		user: user ? user._id : null,
		userName: userNameOf(user),
		location: pitch.location,
		type,
		title: (TYPE_TITLES[type] || 'Inventaire') + ' ' + pitch.location,
		detail: [tour ? tour.name : null, version > 1 ? 'version ' + version : null].filter(Boolean).join(', '),
		result: inventoryResult(summary),
		tourId: tour ? tour._id : null,
		inventoryId,
		version: version || 1,
		snapshot
	}
}

export function interventionEntry({ doc, kindTitle, pitch, user, time, inventoryEntryId })
{
	return {
		id: nextId('intervention', time),
		kind: 'intervention',
		time,
		user: user ? user._id : null,
		userName: userNameOf(user),
		location: pitch.location,
		title: 'Intervention créée',
		detail: kindTitle + ' ' + pitch.location + ', ' + doc.type,
		result: null,
		inventoryEntryId: inventoryEntryId || null,
		label: doc.label,
		interventionType: doc.type
	}
}

export function tourEntry({ tour, action, count, user, time })
{
	return {
		id: nextId('tour', time),
		kind: 'tour',
		time,
		user: user ? user._id : null,
		userName: userNameOf(user),
		location: '',
		title: tour.name,
		detail: action === 'end' ? 'Terminé, ' + plural(count, 'emplacement') : 'Démarré',
		result: null,
		tourId: tour._id
	}
}

export function commentEntry({ pitch, type, user, time, inventoryEntryId, text })
{
	return {
		id: nextId('comment', time),
		kind: 'comment',
		time,
		user: user ? user._id : null,
		userName: userNameOf(user),
		location: pitch.location,
		title: 'Commentaire sur ' + pitch.location,
		detail: (TYPE_TITLES[type] || 'Inventaire') + ' ' + pitch.location,
		result: null,
		inventoryEntryId: inventoryEntryId || null,
		text: text || ''
	}
}

// Filtre, recherche par emplacement (« m066 » trouve « M066 »), personne (onglet Équipe)
export function filterEntries(entries, { filter, query, userId, person })
{
	const definition = FILTERS.find(item => item.id === filter) || FILTERS[0]
	const needle = normalizeLocation(query || '')
	return entries.filter(entry => {
		if (userId && entry.user !== userId)
			return false
		if (person && entry.user !== person)
			return false
		if (definition.kinds && !definition.kinds.includes(entry.kind))
			return false
		if (needle && !normalizeLocation(entry.location || '').includes(needle))
			return false
		return true
	})
}

export function filterCounts(entries, scope)
{
	const counts = {}
	FILTERS.forEach(item => {
		counts[item.id] = filterEntries(entries, { ...scope, filter: item.id }).length
	})
	return counts
}

// « Aujourd'hui », « Hier », puis « 19 oct. »
export function dayLabel(seconds, now)
{
	const day = new Date(seconds * 1000)
	const today = new Date(now * 1000)
	const start = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime() / 1000
	const diff = Math.round((start(today) - start(day)) / DAY)
	if (diff === 0)
		return 'Aujourd\'hui'
	if (diff === 1)
		return 'Hier'
	return formatShortDate(seconds, now)
}

// Groupes par jour, les plus récents en haut : [{ label, entries }]
export function groupByDay(entries, now)
{
	const sorted = entries.slice().sort((a, b) => b.time - a.time)
	const groups = []
	sorted.forEach(entry => {
		const label = dayLabel(entry.time, now)
		const last = groups[groups.length - 1]
		if (last && last.label === label)
			last.entries.push(entry)
		else
			groups.push({ label, entries: [entry] })
	})
	return groups
}

// Deuxième ligne d'une action : détail puis heure, séparés par une virgule (charte : pas de « · »)
export function entrySubtitle(entry)
{
	return [entry.detail, timeLabel(entry.time)].filter(Boolean).join(', ')
}

export function people(entries)
{
	const byId = {}
	entries.forEach(entry => {
		if (entry.user && !byId[entry.user])
			byId[entry.user] = entry.userName
	})
	return Object.keys(byId).map(id => ({ id, name: byId[id] }))
}

// Ce qui a suivi l'enregistrement (A4) : interventions et commentaires ajoutés ensuite, reliés par le journal
export function followUpsOf(entries, inventoryEntry)
{
	const linked = entries.filter(entry => entry.inventoryEntryId === inventoryEntry.id)
	return {
		interventions: linked.filter(entry => entry.kind === 'intervention').map(entry => ({ label: entry.label || entry.detail, type: entry.interventionType || '', time: entry.time })),
		comments: linked.filter(entry => entry.kind === 'comment' && entry.text).map(entry => ({ text: entry.text, user: entry.user, time: entry.time, theme: null }))
	}
}

// Je modifie mes propres inventaires, jamais ceux des autres (décisions 10 et 31)
export function canEditEntry(entry, user)
{
	return entry.kind === 'inventory' && !!user && entry.user === user._id
}

// Inventaire tel qu'il a été enregistré, pour le rouvrir depuis Mon activité (A4). Les photos ne
// gardent que leur description : l'image elle-même vivra sur le serveur (file_id).
export function inventorySnapshot({ type, pitch, stay, client, themes, counts, reference, notes, summary, interventions, user, time, version })
{
	const allThemes = themes || []
	return {
		type,
		pitch: { _id: pitch._id || null, location: pitch.location, location_category: pitch.location_category || '', type: pitch.type || '' },
		stay: stay ? { stay_number: stay.stay_number, location: stay.location, location_category: stay.location_category, arrival_time: stay.arrival_time, departure_time: stay.departure_time, client_number: stay.client_number } : null,
		client: client ? { firstname: client.firstname || '', lastname: client.lastname || '', client_number: client.client_number } : null,
		location: pitch.location,
		stayNumber: stay ? stay.stay_number : null,
		guest: guestName(client),
		referenceLabel: reference ? referenceLabel(reference, time) : null,
		banner: summary ? summary.banner : null,
		gapsTitle: summary ? summary.gapsTitle : null,
		gaps: summary ? summary.gaps.map(item => ({ name: item.object.name, line: item.line, badge: item.badge, theme: item.theme.name })) : [],
		othersText: summary ? summary.othersText : null,
		missingTitle: summary ? summary.missingTitle : null,
		missingHint: summary ? summary.missingHint : null,
		missing: summary ? summary.missing.map(item => ({ name: item.object.name, line: item.line, badge: item.badge, theme: item.theme.name })) : [],
		comments: duringCommentItems(allThemes, notes).map(item => ({ text: item.comment.comment, user: item.comment.commentUser, time: item.comment.commentTime, theme: item.themeName })),
		photos: duringPhotoItems(allThemes, notes).map(item => ({ description: item.photo.description, user: item.photo.user, time: item.photo.time, theme: item.themeName })),
		counts: allThemes.length > 0 && reference ? [].concat(...allThemes.map(theme => theme.objects.map(object => ({ theme: theme.name, name: object.name, count: objectCount(object, counts, reference, true) })))) : [],
		interventions: (interventions || []).map(item => ({ label: item.doc.label, type: item.doc.type, time: item.doc.created_time })),
		user: user ? user._id : null,
		userName: user ? ((user.firstname || '') + ' ' + (user.lastname || '')).trim() : '',
		time,
		version: version || 1
	}
}

// Rouvrir pour modifier (A4) : comptages remis par thème et par nom d'objet, le modèle ayant pu bouger
export function restoredCounts(themes, snapshot)
{
	const byName = {}
	;(snapshot.counts || []).forEach(item => {
		byName[item.theme + '|' + item.name] = item.count
	})
	const counts = {}
	themes.forEach(theme => {
		theme.objects.forEach(object => {
			const count = byName[theme.name + '|' + object.name]
			if (count !== undefined)
				counts[object.id] = count
		})
	})
	return counts
}
